import { useState, useEffect, useRef } from 'react';
import type { WizardFormData } from '../page';

type EmailExistsMode = 'none' | 'exists_incomplete' | 'exists_complete' | 'recover' | 'recover_sent';

interface Props {
  formData: WizardFormData;
  onChange: (updates: Partial<WizardFormData>) => void;
  onNext: () => void;
  error?: string;
  loading?: boolean;
  emailExistsMode: EmailExistsMode;
  resumePassword: string;
  onResumePasswordChange: (v: string) => void;
  onResumeExisting: () => void;
  onPasswordRecovery: () => void;
  onGoToLogin: () => void;
  onResetEmailExistsMode: () => void;
  resumeLoading: boolean;
  resumeError: string;
  onGoogleSignup?: () => void;
  googleLoading?: boolean;
}

export const WizardStep1 = ({
  formData,
  onChange,
  onNext,
  error: externalError,
  loading = false,
  emailExistsMode,
  resumePassword,
  onResumePasswordChange,
  onResumeExisting,
  onPasswordRecovery,
  onGoToLogin,
  onResetEmailExistsMode,
  resumeLoading,
  resumeError,
  onGoogleSignup,
  googleLoading = false,
}: Props) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Local ref: prevents double-click even if React state update is slightly delayed
  const submittingRef = useRef(false);

  // Reset local guard whenever the parent loading state goes back to false
  useEffect(() => {
    if (!loading) {
      submittingRef.current = false;
    }
  }, [loading]);

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!formData.name.trim()) errs.name = 'Nome é obrigatório';
    if (!formData.email.trim()) errs.email = 'E-mail é obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'E-mail inválido';
    if (!formData.password) errs.password = 'Senha é obrigatória';
    else if (formData.password.length < 6) errs.password = 'Mínimo 6 caracteres';
    if (!formData.confirmPassword) errs.confirmPassword = 'Confirme sua senha';
    else if (formData.confirmPassword !== formData.password) errs.confirmPassword = 'Senhas não coincidem';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    // Prevent double-click / concurrent submission
    if (submittingRef.current || loading) return;
    if (!validate()) return;
    submittingRef.current = true;
    onNext();
  };

  // ─── E-mail exists — incomplete account ─────────────────────────────────────
  if (emailExistsMode === 'exists_incomplete' || emailExistsMode === 'exists_complete') {
    const isConfirmedComplete = emailExistsMode === 'exists_complete';
    return (
      <div className="p-8">
        <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-50 mb-5 mx-auto">
          <i className="ri-user-search-line text-2xl text-amber-500" />
        </div>
        <div className="text-center mb-6">
          <h2 className="text-xl font-700 text-gray-900 mb-2">
            {isConfirmedComplete ? 'Conta já ativa' : 'E-mail já cadastrado'}
          </h2>
          <p className="text-sm text-gray-500 leading-relaxed">
            {isConfirmedComplete
              ? `O e-mail ${formData.email} já possui uma conta ativa. Faça login ou recupere sua senha.`
              : `O e-mail ${formData.email} já está cadastrado. Digite sua senha para entrar ou continuar de onde parou.`}
          </p>
        </div>

        {!isConfirmedComplete && (
          <div className="mb-5">
            <label className="block text-sm font-500 text-gray-700 mb-1.5">
              Sua senha
            </label>
            <div className="relative">
              <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
              <input
                type="password"
                value={resumePassword}
                onChange={(e) => onResumePasswordChange(e.target.value)}
                placeholder="Digite sua senha"
                disabled={resumeLoading}
                onKeyDown={(e) => { if (e.key === 'Enter') onResumeExisting(); }}
                className="w-full pl-10 pr-4 h-12 border border-gray-200 rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-400 placeholder:text-gray-400 transition-all disabled:opacity-60"
              />
            </div>
            {resumeError && (
              <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                <i className="ri-error-warning-line text-xs" />
                {resumeError}
              </p>
            )}
          </div>
        )}

        <div className="flex flex-col gap-3">
          {!isConfirmedComplete && (
            <button
              onClick={onResumeExisting}
              disabled={resumeLoading || !resumePassword}
              className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {resumeLoading ? (
                <><i className="ri-loader-4-line animate-spin text-base" />Verificando...</>
              ) : (
                <><i className="ri-play-circle-line text-base" />Entrar / Continuar cadastro</>
              )}
            </button>
          )}

          <button
            onClick={onGoToLogin}
            className="w-full h-12 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-login-circle-line text-base" />
            Fazer login
          </button>

          <button
            onClick={onPasswordRecovery}
            disabled={resumeLoading}
            className="w-full h-11 text-sm text-emerald-600 hover:text-emerald-700 font-500 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-70"
          >
            <i className="ri-key-2-line text-sm" />
            Esqueci minha senha
          </button>
        </div>

        <button
          onClick={onResetEmailExistsMode}
          className="mt-4 w-full text-sm text-gray-400 hover:text-gray-600 text-center cursor-pointer transition-colors"
        >
          Usar outro e-mail
        </button>
      </div>
    );
  }

  // ─── Password recovery sent ──────────────────────────────────────────────────
  if (emailExistsMode === 'recover_sent') {
    return (
      <div className="p-8 text-center">
        <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-50 mb-5 mx-auto">
          <i className="ri-mail-check-line text-3xl text-emerald-500" />
        </div>
        <h2 className="text-xl font-700 text-gray-900 mb-2">E-mail enviado!</h2>
        <p className="text-sm text-gray-500 leading-relaxed mb-6">
          Enviamos um link de redefinição de senha para{' '}
          <span className="font-600 text-gray-700">{formData.email}</span>.
          Verifique sua caixa de entrada e o spam.
        </p>
        <button
          onClick={onGoToLogin}
          className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
        >
          <i className="ri-login-circle-line text-base" />
          Ir para o login
        </button>
        <button
          onClick={onResetEmailExistsMode}
          className="mt-3 w-full text-sm text-gray-400 hover:text-gray-600 text-center cursor-pointer transition-colors"
        >
          Tentar com outro e-mail
        </button>
      </div>
    );
  }

  // ─── Password recovery mode ──────────────────────────────────────────────────
  if (emailExistsMode === 'recover') {
    return (
      <div className="p-8">
        <div className="mb-6">
          <h2 className="text-2xl font-700 text-gray-900 mb-1">Recuperar senha</h2>
          <p className="text-gray-500 text-sm">
            Enviaremos um link de redefinição para {formData.email}
          </p>
        </div>

        {resumeError && (
          <div className="mb-4 p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2.5">
            <i className="ri-error-warning-line text-red-500 text-base mt-0.5 shrink-0" />
            <span className="text-sm text-red-700">{resumeError}</span>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <button
            onClick={onPasswordRecovery}
            disabled={resumeLoading}
            className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-70"
          >
            {resumeLoading ? (
              <><i className="ri-loader-4-line animate-spin text-base" />Enviando...</>
            ) : (
              <><i className="ri-send-plane-line text-base" />Enviar link de recuperação</>
            )}
          </button>
          <button
            onClick={onResetEmailExistsMode}
            className="w-full h-11 border border-gray-200 text-gray-700 font-600 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  // ─── Default: signup form ────────────────────────────────────────────────────
  return (
    <div className="p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-700 text-gray-900 mb-1">Crie sua conta</h2>
        <p className="text-gray-500 text-sm">Preencha suas informações de acesso</p>
      </div>

      {externalError && (
        <div className="mb-4 p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2.5">
          <i className="ri-error-warning-line text-red-500 text-base mt-0.5 shrink-0" />
          <span className="text-sm text-red-700">{externalError}</span>
        </div>
      )}

      {/* Google signup button */}
      {onGoogleSignup && (
        <>
          <button
            type="button"
            onClick={onGoogleSignup}
            disabled={googleLoading || loading}
            className="w-full h-12 border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700 font-600 rounded-xl transition-all text-[15px] cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-3 mb-4"
          >
            {googleLoading ? (
              <><i className="ri-loader-4-line animate-spin text-base" />Conectando com Google...</>
            ) : (
              <>
                <img
                  src="https://readdy.ai/api/search-image?query=Google%20G%20logo%20colorful%20red%20blue%20yellow%20green%20simple%20icon%20flat%20design%20transparent%20background&width=20&height=20&seq=google_logo_wizard&orientation=squarish"
                  alt="Google"
                  className="w-5 h-5 object-contain"
                />
                Cadastrar com Google
              </>
            )}
          </button>

          <div className="mb-4 flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 font-500">ou cadastre com e-mail</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
        </>
      )}

      <div className="space-y-4">
        {([
          { key: 'name', label: 'Como quer ser chamado(a)?', placeholder: 'Ex: Carlos', icon: 'ri-user-smile-line', type: 'text' },
          { key: 'email', label: 'E-mail', placeholder: 'seu@email.com', icon: 'ri-mail-line', type: 'email' },
          { key: 'password', label: 'Senha', placeholder: '••••••••', icon: 'ri-lock-line', type: 'password' },
          { key: 'confirmPassword', label: 'Confirmar senha', placeholder: '••••••••', icon: 'ri-lock-check-line', type: 'password' },
        ] as const).map((field) => (
          <div key={field.key}>
            <label className="block text-sm font-500 text-gray-700 mb-1.5">{field.label}</label>
            <div className="relative">
              <i className={`${field.icon} absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base`} />
              <input
                type={field.type}
                value={formData[field.key]}
                onChange={(e) => onChange({ [field.key]: e.target.value })}
                placeholder={field.placeholder}
                disabled={loading}
                onKeyDown={(e) => { if (e.key === 'Enter' && field.key === 'confirmPassword') handleNext(); }}
                className={`w-full pl-10 pr-10 h-12 border rounded-xl text-sm text-gray-800 outline-none transition-all placeholder:text-gray-400 disabled:opacity-60 disabled:cursor-not-allowed ${
                  errors[field.key]
                    ? 'border-red-300 focus:ring-2 focus:ring-red-100'
                    : 'border-gray-200 focus:ring-2 focus:ring-emerald-100 focus:border-emerald-400'
                }`}
              />
              {formData[field.key] && !errors[field.key] && (
                <i className="ri-check-line absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-400 text-base" />
              )}
            </div>
            {errors[field.key] && (
              <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                <i className="ri-error-warning-line text-xs" />
                {errors[field.key]}
              </p>
            )}
          </div>
        ))}
      </div>

      <button
        onClick={handleNext}
        disabled={loading}
        className="mt-6 w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-70"
      >
        {loading ? (
          <><i className="ri-loader-4-line animate-spin text-base" />Criando conta...</>
        ) : (
          <>Continuar<i className="ri-arrow-right-line text-base" /></>
        )}
      </button>
    </div>
  );
};
